import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, fmtInt } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { Pencil, Plus, Sparkles } from "lucide-react";
import { buildBudgetRows } from "@/lib/budgetRows";
import { NewBudgetDialog } from "@/components/budget/NewBudgetDialog";
import { RealisationView } from "@/components/budget/RealisationView";

// Vergelijking naast elkaar: Begroting Y-1 · Resultaat Y-1 · Begroting Y, met dezelfde
// gedetailleerde rijen als de Begroting-matrix en Voorgaande jaren (lib/budgetRows.js).
export default function BudgetComparisonView({ clubId, club, userTier, canEdit = true, onOpenBudget }) {
  const t = useT();
  const [budgets, setBudgets] = useState([]);
  const [historicals, setHistoricals] = useState([]);
  const [subposts, setSubposts] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState("");
  const [loading, setLoading] = useState(true);
  const [targetYear, setTargetYear] = useState(null);
  const [creatingYear, setCreatingYear] = useState(null);
  const [mode, setMode] = useState("prev"); // prev = begroting vs vorig jaar · real = begroting vs realisatie
  const [realYear, setRealYear] = useState(null);

  const load = () => {
    setLoading(true);
    return Promise.all([
      api.get(`/clubs/${clubId}/budgets`),
      api.get(`/clubs/${clubId}/historicals`),
      api.get(`/clubs/${clubId}/scenarios`),
      api.get(`/clubs/${clubId}/subposts`),
    ])
      .then(([bRes, hRes, sRes, spRes]) => {
        setBudgets(bRes.data || []);
        setHistoricals(hRes.data || []);
        setScenarios(sRes.data || []);
        setSubposts(spRes.data || []);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || String(e.message || e)))
      .finally(() => setLoading(false));
  };

  const reloadBudgets = () => api.get(`/clubs/${clubId}/budgets`).then(({ data }) => setBudgets(data || []));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [clubId]);

  useEffect(() => {
    if (targetYear !== null) return;
    if (budgets.length) setTargetYear(Math.max(...budgets.map((b) => b.year)));
    else if (historicals.length) setTargetYear(Math.max(...historicals.map((h) => h.year)) + 1);
  }, [budgets, historicals, targetYear]);

  const budgetByYear = useMemo(() => Object.fromEntries(budgets.map((b) => [b.year, b])), [budgets]);
  const histByYear = useMemo(() => Object.fromEntries(historicals.map((h) => [h.year, h])), [historicals]);

  const targetBudget = targetYear !== null ? budgetByYear[targetYear] : null;
  const prevYear = targetYear !== null ? targetYear - 1 : null;
  const prevBudget = prevYear !== null ? budgetByYear[prevYear] : null;
  const prevActual = prevYear !== null ? histByYear[prevYear] : null;

  const rows = useMemo(
    () => buildBudgetRows(t, subposts, [prevBudget, prevActual, targetBudget].filter(Boolean)),
    [t, subposts, prevBudget, prevActual, targetBudget],
  );

  const valueOf = (row, obj) => (obj ? row.value(obj) : null);
  const driftPct = (base, next) => (base === null || next === null || !base ? null : ((next - base) / Math.abs(base)) * 100);
  const driftEur = (base, next) => (base === null || next === null ? null : next - base);
  const eur = (v) => (v === null ? "—" : fmtInt(v));
  const fmtEurDelta = (v) => (v === null ? "—" : `${v > 0 ? "+" : v < 0 ? "−" : ""}${fmtInt(Math.abs(v))}`);
  const fmtPct = (p) => (p === null || Number.isNaN(p) ? "—" : `${p > 0 ? "+" : p < 0 ? "−" : ""}${Math.abs(p).toFixed(1)}%`);

  const availableTargetYears = useMemo(() => budgets.map((b) => b.year).sort((a, z) => z - a), [budgets]);
  const realisationYears = useMemo(
    () => budgets.map((b) => b.year).filter((y) => histByYear[y]).sort((a, z) => z - a),
    [budgets, histByYear],
  );
  const realY = realYear ?? realisationYears[0] ?? null;
  const realBudget = realY !== null ? budgetByYear[realY] : null;
  const realActual = realY !== null ? histByYear[realY] : null;
  const realRows = useMemo(
    () => buildBudgetRows(t, subposts, [realBudget, realActual].filter(Boolean)),
    [t, subposts, realBudget, realActual],
  );

  if (loading) return <div className="text-sm text-muted-foreground">{t("Laden…")}</div>;

  if (!budgets.length && !historicals.length) {
    return (
      <div className="border-2 border-dashed border-foreground/40 p-10 text-center" data-testid="budget-comparison-empty">
        <div className="font-display uppercase font-bold text-xl mt-2">{t("Nog geen begrotingen of jaarrekeningen")}</div>
        <p className="text-muted-foreground max-w-lg mx-auto mt-2">
          {t("Voer eerst het laatste boekjaar in bij 'Voorgaande jaren' — dan kun je hier de vergelijking maken.")}
        </p>
      </div>
    );
  }

  const noPrevData = !prevBudget && !prevActual;
  const selectedScenario = scenarios.find((s) => s.id === selectedScenarioId) || null;

  const applyFromScenario = async (targetYr) => {
    if (!selectedScenario) return;
    try {
      const { data } = await api.get(`/scenarios/${selectedScenario.id}/compute`);
      const row = (data.years || []).find((y) => y.actual_year === targetYr);
      if (!row) { toast.error(t("Scenario dekt jaar {y} niet.").replace("{y}", String(targetYr))); return; }

      // Subposten meeschalen vanaf de scenario-baseline (zelfde groeivoeten als compute.py).
      const baseSource = data.baseline_source;
      const baseYear = data.baseline_actual_year;
      let baseDoc = null;
      if (baseSource === "budget" && baseYear != null) baseDoc = budgetByYear[baseYear] || null;
      else if (baseSource === "last_historical" && baseYear != null) baseDoc = histByYear[baseYear] || null;
      const gap = baseYear != null ? Math.max(0, targetYr - baseYear) : 0;
      const inflation = Number(selectedScenario.inflation_rate ?? 0.02);
      const spGrowth = selectedScenario.subpost_growth || {};
      const catGrowth = {
        revenue: Number(selectedScenario.revenue_growth_rate ?? inflation),
        opex: Number(selectedScenario.opex_growth_rate ?? inflation),
        other_income: Number(selectedScenario.other_income_growth_rate ?? inflation),
      };
      const projectSubposts = (list, category) => (list || []).map((it) => {
        const nm = String(it?.name || "").trim();
        const ov = (spGrowth[category] || {})[nm];
        const g = ov !== undefined && ov !== null ? Number(ov) : catGrowth[category];
        return { name: nm, amount: Math.round(Number(it.amount || 0) * Math.pow(1 + g, gap)) };
      });
      const notesParts = [`Gegenereerd uit scenario '${selectedScenario.name}' (${new Date().toLocaleDateString("nl-NL")}).`];
      if (baseDoc && gap > 0) {
        notesParts.push(t("Subposten geprojecteerd vanaf {src} {y} over {n} jaar.")
          .replace("{src}", baseSource === "budget" ? t("Begroting") : t("Resultaat"))
          .replace("{y}", String(baseYear)).replace("{n}", String(gap)));
      }
      const body = {
        year: targetYr,
        revenue: Math.round(row.revenue || 0),
        operating_costs: Math.round(row.opex || 0),
        depreciation: Math.round(row.depreciation || 0),
        interest_paid: Math.round(row.interest || 0),
        interest_income: Math.round(Number(baseDoc?.interest_income || 0) * Math.pow(1 + inflation, gap)),
        other_income: Math.round(row.other_income || 0),
        cash_bank_balance: Math.round(row.cash_position || 0),
        revenue_breakdown: projectSubposts(baseDoc?.revenue_breakdown, "revenue"),
        opex_breakdown: projectSubposts(baseDoc?.opex_breakdown, "opex"),
        other_income_breakdown: projectSubposts(baseDoc?.other_income_breakdown, "other_income"),
        notes: notesParts.join(" "),
      };
      const existing = budgetByYear[targetYr];
      if (existing) {
        if (existing.locked) { toast.error(t("Begroting {y} is vergrendeld — ontgrendel eerst.").replace("{y}", String(targetYr))); return; }
        if (!window.confirm(t("Overschrijf bestaande begroting {y}?").replace("{y}", String(targetYr)))) return;
        await api.put(`/budgets/${existing.id}`, body);
        toast.success(t("Begroting {y} bijgewerkt uit scenario.").replace("{y}", String(targetYr)));
      } else {
        await api.post(`/clubs/${clubId}/budgets`, body);
        toast.success(t("Begroting {y} aangemaakt uit scenario.").replace("{y}", String(targetYr)));
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || String(e.message || e));
    }
  };

  const editBtn = (b, testid) => (
    <button onClick={() => onOpenBudget?.()} data-testid={testid} disabled={b?.locked}
      title={t("Bewerken in tabblad Begroting")}
      className="opacity-70 hover:opacity-100 hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed">
      <Pencil className="w-3 h-3" />
    </button>
  );
  const cellCls = (row, extra = "") => `text-right px-3 py-1.5 font-mono-num ${row.kind === "sub" || row.kind === "sys" ? "text-sm text-muted-foreground" : ""} ${row.strong ? "font-bold" : ""} ${extra}`;
  const driftCls = (dp, muted) => (dp === null ? "" : Math.abs(dp) > 20 ? "text-destructive font-bold" : muted ? "text-muted-foreground" : "");

  return (
    <div className="space-y-4" data-testid="budget-comparison-view">
      <div className="flex gap-2 flex-wrap" data-testid="cmp-mode-switch">
        {[["prev", t("Begroting vs vorig jaar")], ["real", t("Begroting vs realisatie")]].map(([k, l]) => (
          <button key={k} onClick={() => setMode(k)} data-testid={`cmp-mode-${k}`}
            className={`px-3 py-1.5 font-display uppercase font-bold text-xs tracking-widest border-2 border-foreground ${mode === k ? "bg-foreground text-background" : "bg-background text-foreground hover:bg-muted"}`}>
            {l}
          </button>
        ))}
      </div>
      {mode === "real" && (
        <RealisationView t={t} clubId={clubId} club={club} userTier={userTier} canEdit={canEdit}
          years={realisationYears} year={realY} onYear={setRealYear} rows={realRows}
          budget={realBudget} actual={realActual} eur={eur} fmtEurDelta={fmtEurDelta} fmtPct={fmtPct} onSaved={reloadBudgets} />
      )}
      <div className={`flex flex-wrap items-start justify-between gap-3 ${mode === "real" ? "hidden" : ""}`}>
        <div>
          <div className="font-display uppercase font-black text-2xl tracking-tight">{t("Vergelijking naast elkaar")}</div>
          <p className="text-sm text-muted-foreground max-w-3xl">
            {t("Begroting van vorig jaar, het uiteindelijke resultaat van vorig jaar en de begroting van dit jaar in één tabel — met dezelfde posten en subposten als de Begroting-matrix. Δ rechts toont hoe je nieuwe begroting afwijkt van het feitelijke resultaat.")}
          </p>
        </div>
        <div className="flex flex-wrap items-start gap-3">
          <div>
            <label className="stat-label block" htmlFor="cmp-scenario">{t("Gebaseerd op scenario")}</label>
            <select id="cmp-scenario" value={selectedScenarioId} onChange={(e) => setSelectedScenarioId(e.target.value)}
              data-testid="budget-comparison-scenario-picker" className="mt-1 border-2 border-foreground bg-background px-3 py-2 min-w-[220px]">
              <option value="">{t("— Kies scenario —")}</option>
              {scenarios.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {selectedScenario && targetYear !== null && (
              <button onClick={() => applyFromScenario(targetYear)} data-testid="apply-scenario-to-target-btn"
                className="mt-2 btn-brutal bg-primary text-primary-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> {t("Toepassen op {y}").replace("{y}", String(targetYear))}
              </button>
            )}
          </div>
          <div>
            <label className="stat-label block" htmlFor="cmp-year">{t("Begroting jaar Y")}</label>
            <select id="cmp-year" value={targetYear ?? ""} onChange={(e) => setTargetYear(parseInt(e.target.value, 10))}
              data-testid="budget-comparison-year-picker" className="mt-1 border-2 border-foreground bg-background px-3 py-2 font-mono-num">
              {[...new Set([...availableTargetYears, ...(historicals.length ? [Math.max(...historicals.map((h) => h.year)) + 1] : [])])]
                .sort((a, z) => z - a).map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            {!prevBudget && prevYear !== null && !noPrevData && (
              <button onClick={() => setCreatingYear(prevYear)} data-testid="create-budget-prev"
                className="mt-2 block text-[11px] font-display uppercase font-bold tracking-widest text-primary hover:underline">
                <Plus className="w-3 h-3 inline -mt-0.5" /> {t("Begroting {y} toevoegen").replace("{y}", String(prevYear))}
              </button>
            )}
          </div>
        </div>
      </div>

      {mode === "real" ? null : noPrevData ? (
        <div className="border-2 border-dashed border-foreground/40 p-8 text-center" data-testid="budget-comparison-no-prev">
          <div className="font-display uppercase font-bold text-lg">{t("Geen data voor jaar {y}").replace("{y}", String(prevYear))}</div>
          <p className="text-muted-foreground text-sm mt-1">
            {t("Voeg een begroting of jaarrekening voor {y} toe om de vergelijking te maken.").replace("{y}", String(prevYear))}
          </p>
        </div>
      ) : (
        <div className="border-2 border-foreground bg-card heavy-shadow overflow-x-auto">
          <table className="w-full text-sm" data-testid="budget-comparison-table">
            <thead className="bg-muted/50 border-b-2 border-foreground">
              <tr>
                <th className="text-left px-3 py-2 stat-label sticky left-0 sticky-tint-50 min-w-[150px] md:min-w-[220px]">{t("Post")}</th>
                {prevBudget && (
                  <th className="text-right px-3 py-2 stat-label min-w-[140px]" data-testid="col-budget-prev">
                    <div className="inline-flex items-center gap-2 justify-end"><span>{t("Begroting")} {prevYear}</span>{editBtn(prevBudget, "edit-budget-prev")}</div>
                  </th>
                )}
                <th className="text-right px-3 py-2 stat-label min-w-[140px] bg-muted/70" data-testid="col-actual-prev">{t("Resultaat")} {prevYear}</th>
                <th className="text-right px-3 py-2 stat-label min-w-[140px]" data-testid="col-budget-target">
                  <div className="inline-flex items-center gap-2 justify-end">
                    <span>{t("Begroting")} {targetYear}</span>
                    {targetBudget ? editBtn(targetBudget, "edit-budget-target") : targetYear !== null ? (
                      <button onClick={() => setCreatingYear(targetYear)} data-testid="create-budget-target" title={t("Aanmaken")}
                        className="opacity-70 hover:opacity-100 hover:text-primary"><Plus className="w-3 h-3" /></button>
                    ) : null}
                  </div>
                </th>
                <th className="text-right px-3 py-2 stat-label min-w-[120px]" data-testid="col-drift-eur">{t("Δ € vs resultaat")}</th>
                <th className="text-right px-3 py-2 stat-label min-w-[100px]" data-testid="col-drift">{t("Δ% vs resultaat")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const bp = valueOf(row, prevBudget);
                const ap = valueOf(row, prevActual);
                const bt = valueOf(row, targetBudget);
                const dp = driftPct(ap, bt);
                const de = driftEur(ap, bt);
                const muted = row.kind === "sub" || row.kind === "sys";
                const trCls = row.kind === "computed" ? "border-t-2 border-foreground bg-muted/40" : muted ? "border-b border-foreground/5 bg-muted/10" : "border-b border-foreground/10";
                const labelCls = muted ? "pl-8 pr-3 py-1.5 sticky left-0 sticky-tint-10 text-sm text-muted-foreground italic"
                  : row.kind === "computed" ? "px-3 py-2 sticky left-0 sticky-tint-40 font-display uppercase font-black"
                  : "px-3 py-2 sticky left-0 bg-card font-bold";
                return (
                  <tr key={row.key} className={trCls} data-testid={`cmp-row-${row.key}`}>
                    <td className={labelCls}>{row.label}{row.kind === "sys" && <span className="ml-2 not-italic text-[9px] font-display uppercase tracking-widest text-muted-foreground/50">{t("SYS")}</span>}</td>
                    {prevBudget && <td className={cellCls(row)} data-testid={`cmp-${row.key}-budget-prev`}>{eur(bp)}</td>}
                    <td className={cellCls(row, "bg-muted/20")} data-testid={`cmp-${row.key}-actual-prev`}>{eur(ap)}</td>
                    <td className={cellCls(row)} data-testid={`cmp-${row.key}-budget-target`}>{eur(bt)}</td>
                    <td className={cellCls(row, driftCls(dp, muted))} data-testid={`cmp-${row.key}-drift-eur`}>{fmtEurDelta(de)}</td>
                    <td className={cellCls(row, driftCls(dp, muted))} data-testid={`cmp-${row.key}-drift`}>{fmtPct(dp)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {mode === "prev" && (
        <p className="text-xs text-muted-foreground">
          {t("Δ-cellen kleuren rood zodra de afwijking ≥ 20% is. Cijfers bewerk je in het tabblad Begroting (potlood-icoon).")}
        </p>
      )}

      <NewBudgetDialog open={creatingYear !== null} onOpenChange={(v) => { if (!v) setCreatingYear(null); }}
        clubId={clubId} historicals={historicals} budgets={budgets} suggestedYear={creatingYear ?? targetYear ?? new Date().getFullYear() + 1}
        onCreated={() => { load(); setCreatingYear(null); }} />
    </div>
  );
}

